import { SignInRedirect } from "./components/SignInRedirect.js";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button, Spinner } from "@opencloud/ui";
import { conversationContextSummary } from "@opencloud/agent-protocol";
import { useCallback, useEffect, useRef, useState } from "react";
import { personalAiAccountsAllowed, type AgentBootstrap } from "./bootstrap.js";
import {
  AgentApiError,
  api,
  browserRequestId,
  followOperation,
  requestBody,
  subscribeApiReachability,
} from "./api.js";
import {
  AgentNotificationPrompt,
  shouldOfferAgentNotifications,
} from "./components/AgentNotifications.js";
import { appAiDashboardUrl } from "./app-ai.js";
import { ConversationChat } from "./components/ConversationChat.js";
import { HostedAgentUpgrade } from "./components/HostedAgentUpgrade.js";
import { ProviderSetup } from "./components/ProviderSetup.js";
import { PlatformConversationReview } from "./components/PlatformConversationReview.js";
import { ReleaseHistory } from "./components/ReleaseHistory.js";
import {
  latestActiveRoot,
} from "./presentation.js";
import type {
  AgentAppView,
  AgentRelease,
  ConversationSnapshot,
  ConversationThread,
  HarnessOption,
  HostedAgentView,
  IncidentView,
  OperationView,
  AgentModelProvider,
  ProviderConnectionView,
  ProviderView,
  SessionView,
} from "./types.js";
import { useAgentEvents } from "./use-agent-events.js";
import { acceptsModelChoice, agentConnectionProvider, agentEngineLabels, harnessProvider, usesAppOwnerHarness } from "./harness-provider.js";

type Surface =
  | "loading"
  | "new"
  | "new-settings"
  | "workspace"
  | "settings"
  | "releases";

type AppSurface = Extract<Surface, "workspace" | "settings" | "releases">;

type HistoryMode = "push" | "replace" | "none";

export interface AwaitingAgentReply {
  appId: string;
  threadId: string;
  requestId: string;
  runId: string | null;
  sentAt: string;
  knownAgentMessageIds: string[];
}

export type ConversationStopTarget =
  { kind: "run"; id: string } | { kind: "request"; id: string } | null;

export function resolveConversationStopTarget(
  activeRunId: string | null,
  awaiting: Pick<AwaitingAgentReply, "requestId" | "runId"> | null,
): ConversationStopTarget {
  if (activeRunId) return { kind: "run", id: activeRunId };
  if (awaiting?.runId) return { kind: "run", id: awaiting.runId };
  if (awaiting) return { kind: "request", id: awaiting.requestId };
  return null;
}

interface ConversationView {
  token: number;
  appId: string | null;
  threadId: string | null;
}

const AGENT_HISTORY_INDEX = "opencloudAgentIndex";
/** Browser-side AI choices and prompt state that nothing reads any more. */
const RETIRED_STORAGE_KEYS = [
  "opencloud.agentNewAppSettings",
  "opencloud.unattendedAiConsentDismissed",
  "opencloud.agentOwnAiPromptDismissed",
  // The automatic-repairs notice; failure cards in the chat replace it (decision 0031).
  "opencloud.agentAutomaticAiPromptDismissed",
] as const;

export function AgentApplication({ bootstrap }: { bootstrap: AgentBootstrap }) {
  const route = readRoute();
  if (route.platformReview && route.appId) {
    return <PlatformConversationReview bootstrap={bootstrap} appId={route.appId} />;
  }
  return <OwnerAgentApplication bootstrap={bootstrap} />;
}

function OwnerAgentApplication({ bootstrap }: { bootstrap: AgentBootstrap }) {
  const [surface, setSurface] = useState<Surface>("loading");
  const [session, setSession] = useState<SessionView | null | undefined>(
    undefined,
  );
  const [apps, setApps] = useState<AgentAppView[]>([]);
  const [workingAppIds, setWorkingAppIds] = useState<Set<string>>(new Set());
  const [attentionAppIds, setAttentionAppIds] = useState<Set<string>>(new Set());
  const [harnesses, setHarnesses] = useState<HarnessOption[]>([]);
  const [defaultHarnessVersion, setDefaultHarnessVersion] = useState(
    "opencloud-product-harness-open-v7",
  );
  // The service names the provider a new chat's engine will use; older services do not.
  const [serviceNewChatProvider, setServiceNewChatProvider] = useState<AgentModelProvider | null | undefined>(undefined);
  // It also says whether an account pays for chats, which only it can see for organisation accounts.
  const [serviceAccountPays, setServiceAccountPays] = useState<boolean | undefined>(undefined);
  const [providers, setProviders] = useState<ProviderView[]>([]);
  const [connections, setConnections] = useState<ProviderConnectionView[]>([]);
  const [currentApp, setCurrentApp] = useState<AgentAppView | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(() => new URLSearchParams(location.search).get("conversation"));
  const [threadsByApp, setThreadsByApp] = useState<
    Map<string, ConversationThread | null>
  >(new Map());
  const [currentThread, setCurrentThread] = useState<ConversationThread | null>(
    null,
  );
  const [snapshot, setSnapshot] = useState<ConversationSnapshot | null>(null);
  const [incidents, setIncidents] = useState<IncidentView[]>([]);
  // Only an app's owner reads its incidents. Ask once, not on every update.
  const incidentsRefused = useRef(new Set<string>());
  const [releases, setReleases] = useState<AgentRelease[]>([]);
  const [openReleaseId, setOpenReleaseId] = useState<string | null>(null);
  const [focusMessageId, setFocusMessageId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [offline, setOffline] = useState(false);
  // Nothing runs for a plan without the hosted Agent, so it listens for nothing.
  const planUpgrade = hostedAgentUpgrade(session);
  const agentEvents = useAgentEvents(session !== null && session !== undefined && !planUpgrade, session?.userId);
  const [busy, setBusy] = useState(false);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [releaseError, setReleaseError] = useState<string | null>(null);
  const [notice, setNotice] = useState("");
  const [notificationPromptOpen, setNotificationPromptOpen] = useState(false);
  const noticeTimer = useRef<number | null>(null);
  const releaseRefreshRef = useRef<string | null>(null);
  const warmDraftRef = useRef(false);
  const notificationOfferRef = useRef(false);
  const conversationViewRef = useRef<ConversationView>({
    token: 0,
    appId: null,
    threadId: null,
  });
  const platformAiEnabled =
    bootstrap.platformAiEnabled && session?.platformAiEnabled === true;
  useEffect(() => {
    // AI choices live on the server (the chat's pick, each app's automatic
    // tasks), so earlier browser-side choices and prompt state are forgotten.
    try {
      for (const key of RETIRED_STORAGE_KEYS) window.localStorage.removeItem(key);
    } catch {
      // Storage can be unavailable; nothing reads these keys any more.
    }
  }, []);
  useEffect(() => {
    let offlineTimer: number | null = null;
    const unsubscribe = subscribeApiReachability((reachable) => {
      if (reachable) {
        if (offlineTimer !== null) window.clearTimeout(offlineTimer);
        offlineTimer = null;
        setOffline(false);
        return;
      }
      if (offlineTimer === null) offlineTimer = window.setTimeout(() => {
        offlineTimer = null;
        setOffline(true);
      }, 1_500);
    });
    return () => { unsubscribe(); if (offlineTimer !== null) window.clearTimeout(offlineTimer); };
  }, []);

  const notify = useCallback((message: string) => {
    setNotice(message);
    if (noticeTimer.current !== null) window.clearTimeout(noticeTimer.current);
    noticeTimer.current = window.setTimeout(() => setNotice(""), 4_000);
  }, []);

  const warmAgent = useCallback(() => {
    void api("/v1/warmup", { method: "POST", body: requestBody() }).catch(
      () => undefined,
    );
  }, []);

  const offerNotifications = useCallback(() => {
    if (notificationOfferRef.current) return;
    notificationOfferRef.current = true;
    void shouldOfferAgentNotifications().then((offer) => {
      if (offer) setNotificationPromptOpen(true);
    });
  }, []);

  useEffect(() => {
    if (!session || !draft.trim() || warmDraftRef.current) return;
    warmDraftRef.current = true;
    warmAgent();
  }, [draft, session, warmAgent]);

  useEffect(() => {
    if (!draft.trim()) warmDraftRef.current = false;
  }, [draft]);

  const loadCatalog = useCallback(async () => {
    const [appResponse, providerResponse, connectionResponse] =
      await Promise.all([
        api<{
          apps: AgentAppView[];
          workingAppIds: string[];
          attentionAppIds?: string[];
          harnesses: HarnessOption[];
          defaultHarnessVersion: string;
          newConversationProvider?: AgentModelProvider | null;
          newConversationAccountPays?: boolean;
        }>("/v1/apps"),
        api<{ providers: ProviderView[] }>("/v1/providers"),
        api<{ connections: ProviderConnectionView[] }>(
          "/v1/provider-connections",
        ),
      ]);
    const threadEntries = await Promise.all(
      appResponse.apps.map(async (app) => {
        const response = await api<{ threads: ConversationThread[] }>(
          `/v1/apps/${encodeURIComponent(app.id)}/threads`,
        );
        return [app.id, response.threads[0] ?? null] as const;
      }),
    );
    const nextThreadsByApp = new Map(threadEntries);
    const orderedApps = orderAppsByRecentConversation(
      appResponse.apps,
      nextThreadsByApp,
    );
    setApps(orderedApps);
    setWorkingAppIds(new Set(appResponse.workingAppIds));
    setAttentionAppIds(new Set(appResponse.attentionAppIds ?? []));
    setHarnesses(appResponse.harnesses);
    setDefaultHarnessVersion(appResponse.defaultHarnessVersion);
    setServiceNewChatProvider(appResponse.newConversationProvider);
    setServiceAccountPays(appResponse.newConversationAccountPays);
    setThreadsByApp(nextThreadsByApp);
    setProviders(providerResponse.providers);
    setConnections(connectionResponse.connections);
    return {
      apps: orderedApps,
      threadsByApp: nextThreadsByApp,
      providers: providerResponse.providers,
      connections: connectionResponse.connections,
    };
  }, []);

  const refreshWorkingApps = useCallback(async () => {
    const viewToken = conversationViewRef.current.token;
    const response = await api<{
      apps: AgentAppView[];
      workingAppIds: string[];
      attentionAppIds?: string[];
    }>("/v1/apps");
    if (conversationViewRef.current.token !== viewToken) return;
    setApps((current) => preserveAppOrder(mergeRefreshedApps(response.apps, current), current));
    setWorkingAppIds(new Set(response.workingAppIds));
    setAttentionAppIds(new Set(response.attentionAppIds ?? []));
    const entries = await Promise.all(response.apps.map(async (app) => {
      try {
        const result = await api<{ threads: ConversationThread[] }>(`/v1/apps/${encodeURIComponent(app.id)}/threads`);
        return [app.id, result.threads[0] ?? null] as const;
      } catch {
        return null;
      }
    }));
    if (conversationViewRef.current.token !== viewToken) return;
    setThreadsByApp((current) => {
      const next = new Map(current);
      for (const entry of entries) if (entry) next.set(...entry);
      return next;
    });
  }, []);

  const beginConversationView = useCallback(
    (appId: string | null, threadId: string | null = null) => {
      const next = {
        token: conversationViewRef.current.token + 1,
        appId,
        threadId,
      };
      conversationViewRef.current = next;
      return next.token;
    },
    [],
  );

  const attachConversationThread = useCallback(
    (token: number, appId: string, threadId: string | null) => {
      const current = conversationViewRef.current;
      if (current.token !== token || current.appId !== appId) return false;
      conversationViewRef.current = { ...current, threadId };
      return true;
    },
    [],
  );

  useEffect(() => {
    setCurrentApp((current) => {
      if (!current) return current;
      return apps.find((app) => app.id === current.id) ?? current;
    });
  }, [apps]);

  const loadReleases = useCallback(async (appId: string) => {
    const view = conversationViewRef.current;
    const response = await api<{ releases: AgentRelease[] }>(
      `/v1/apps/${encodeURIComponent(appId)}/releases`,
    );
    if (
      conversationViewRef.current.token !== view.token ||
      conversationViewRef.current.appId !== appId
    ) {
      return response.releases;
    }
    setReleases(response.releases);
    return response.releases;
  }, []);

  // Settings use a bounded read; the mounted chat owns live state and delivery.
  const refreshSnapshot = useCallback(async (app: AgentAppView, thread: ConversationThread) => {
    const view = conversationViewRef.current;
    const next = await api<ConversationSnapshot>(
      `/v1/apps/${app.id}/threads/${thread.id}/snapshot`,
    );
    if (!conversationViewMatches(conversationViewRef.current, app.id, thread.id, view.token)) return;
    setSnapshot(next);
    setCurrentThread(next.thread);
  }, []);

  const receiveSnapshot = useCallback((next: import("./use-conversation.js").ChatSnapshot | null) => {
    if (!next?.app) {
      setSnapshot(null);
      if (next) { setCurrentApp(null); setCurrentThread(null); setSurface("new"); }
      return;
    }
    setSnapshot({ ...next, app: next.app });
    setCurrentThread(next.thread);
    setThreadsByApp(current => new Map(current).set(next.app!.id, next.thread));
  }, []);

  useEffect(() => {
    if (!currentApp || incidentsRefused.current.has(currentApp.id)) return;
    const appId = currentApp.id;
    let cancelled = false;
    void api<{ incidents: IncidentView[] }>(
      `/v1/apps/${appId}/incidents`,
    ).then(value => {
      if (!cancelled) setIncidents(value.incidents);
    }).catch((failure) => {
      if (failure instanceof AgentApiError && failure.status === 403) incidentsRefused.current.add(appId);
    });
    return () => { cancelled = true; };
  }, [currentApp?.id, snapshot?.cursor]);

  const selectApp = useCallback(
    async (
      app: AgentAppView,
      preferredThreadId?: string | null,
      targetSurface: AppSurface = "workspace",
      historyMode: HistoryMode = "push",
      targetMessageId?: string | null,
    ) => {
      const selectionToken = beginConversationView(app.id, preferredThreadId ?? null);
      setCurrentApp(app);
      setConversationId(preferredThreadId ?? null);
      setSnapshot(null);
      setReleases([]);
      releaseRefreshRef.current = null;
      setIncidents([]);
      setFocusMessageId(targetMessageId ?? null);
      const threadResponse = await api<{ threads: ConversationThread[] }>(
        `/v1/apps/${encodeURIComponent(app.id)}/threads`,
      );
      const thread =
        threadResponse.threads.find(
          (candidate) => candidate.id === preferredThreadId,
        ) ??
        threadResponse.threads[0] ??
        null;
      if (!attachConversationThread(selectionToken, app.id, thread?.id ?? null)) {
        return;
      }
      setThreadsByApp((current) => {
        const next = new Map(current);
        next.set(app.id, thread);
        return next;
      });
      setCurrentThread(thread);
      setConversationId(thread?.id ?? null);
      if (targetSurface === "releases") {
        await loadReleases(app.id);
        if (!conversationViewMatches(conversationViewRef.current, app.id, thread?.id ?? null, selectionToken)) return;
        setSurface("releases");
        updateLocation(
          { app: appRouteKey(app), surface: "releases" },
          historyMode,
        );
      } else if (targetSurface === "settings") {
        setSurface("settings");
        updateLocation(
          { app: appRouteKey(app), surface: "settings" },
          historyMode,
        );
        if (thread) await refreshSnapshot(app, thread);
      } else {
        setSurface("workspace");
        updateLocation(
          {
            app: appRouteKey(app),
            ...(targetMessageId && thread ? { thread: thread.id } : {}),
            ...(targetMessageId ? { message: targetMessageId } : {}),
          },
          historyMode,
        );
        if (thread) {
          await loadReleases(app.id);
        }
      }
    },
    [
      attachConversationThread,
      beginConversationView,
      loadReleases,
      refreshSnapshot,
    ],
  );

  const showNewApp = useCallback(
    (historyMode: HistoryMode = "push", resetConversation = true) => {
      beginConversationView(null);
      setCurrentApp(null);
      setCurrentThread(null);
      setSnapshot(null);
      setReleases([]);
      releaseRefreshRef.current = null;
      setOpenReleaseId(null);
      setIncidents([]);
      if (resetConversation) {
        setDraft("");
        setConversationId(new URLSearchParams(window.location.search).get("conversation"));
      }
      setSurface("new");
      updateLocation({}, historyMode);
      warmAgent();
    },
    [beginConversationView, warmAgent],
  );

  const initialize = useCallback(async () => {
    setFatalError(null);
    try {
      const nextSession = await api<SessionView>("/v1/session");
      setSession(nextSession);
      if (hostedAgentUpgrade(nextSession)) {
        // The upgrade screen needs no apps, connections or warm runtime.
        setSurface("new");
        return;
      }
      const catalog = await loadCatalog();
      const route = readRoute();
      const requestedApp = findRouteApp(catalog.apps, route.appId);
      if (requestedApp) {
        await selectApp(
          requestedApp,
          route.threadId,
          route.surface === "releases"
            ? "releases"
            : route.surface === "settings" || route.surface === "provider"
                ? "settings"
                : "workspace",
          "replace",
          route.messageId,
        );
        setOpenReleaseId(route.surface === "releases" ? route.releaseId : null);
      } else if (
        route.surface === "provider" ||
        route.surface === "new-settings" ||
        (route.surface === "settings" && !route.appId)
      ) {
        showNewApp("none");
        setSurface("new-settings");
      } else {
        showNewApp(new URLSearchParams(window.location.search).has("conversation") ? "none" : "replace");
      }
    } catch (error) {
      if (error instanceof AgentApiError && error.status === 401) {
        setSession(null);
        setSurface("new");
      } else {
        setFatalError(
          error instanceof Error ? error.message : "The agent could not open.",
        );
      }
    }
  }, [loadCatalog, selectApp, showNewApp]);

  const restoreLocation = useCallback(async () => {
    const route = readRoute();
    setConversationId(new URLSearchParams(window.location.search).get("conversation"));
    const requestedApp = findRouteApp(apps, route.appId);
    if (requestedApp) {
      await selectApp(
        requestedApp,
        route.threadId,
        route.surface === "releases"
          ? "releases"
          : route.surface === "settings" || route.surface === "provider"
              ? "settings"
              : "workspace",
        "none",
        route.messageId,
      );
      setOpenReleaseId(route.surface === "releases" ? route.releaseId : null);
      return;
    }

    if (
      route.surface === "provider" ||
      route.surface === "new-settings" ||
      (route.surface === "settings" && !route.appId)
    ) {
      showNewApp("none", false);
      setSurface("new-settings");
      return;
    }
    showNewApp(new URLSearchParams(window.location.search).has("conversation") ? "none" : "replace", false);
  }, [apps, selectApp, showNewApp]);

  useEffect(() => {
    ensureAgentHistoryEntry();
    void initialize();
    return () => {
      if (noticeTimer.current !== null)
        window.clearTimeout(noticeTimer.current);
    };
  }, [initialize]);

  useEffect(() => {
    if (!session) return;
    const restore = () => {
      void restoreLocation().catch((error: unknown) =>
        notify(messageOf(error)),
      );
    };
    window.addEventListener("popstate", restore);
    return () => window.removeEventListener("popstate", restore);
  }, [notify, restoreLocation, session]);

  useEffect(() => {
    if (!session || agentEvents.connected) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        void refreshWorkingApps().catch(() => undefined);
      }
    }, 4_000);
    return () => window.clearInterval(timer);
  }, [agentEvents.connected, refreshWorkingApps, session]);

  useEffect(() => agentEvents.subscribe((event) => {
    if (event.type === "conversation.removed") {
      if (!event.appId) return;
      setApps((current) => current.filter((app) => app.id !== event.appId));
      setThreadsByApp((current) => {
        if (!current.has(event.appId!)) return current;
        const next = new Map(current);
        next.delete(event.appId!);
        return next;
      });
      setCurrentApp((current) => current?.id === event.appId ? null : current);
      return;
    }
    if (event.type !== "agent.message.created" || !event.appId) return;
    const message = event.data.message;
    if (!message || typeof message !== "object" || Array.isArray(message)) return;
    const value = message as Record<string, unknown>;
    if (value.presentation !== "conversation" || typeof value.content !== "string" ||
        typeof value.createdAt !== "string" ||
        conversationContextSummary({ role: String(value.role), content: value.content }) !== null) return;
    setThreadsByApp((current) => {
      const existing = current.get(event.appId!);
      if (!existing || existing.id !== event.conversationId) return current;
      const next = new Map(current);
      next.set(event.appId!, { ...existing, latestMessage: value.content as string,
        updatedAt: value.createdAt as string,
        unread: event.conversationId !== conversationId });
      return next;
    });
  }), [agentEvents.subscribe, conversationId]);

  const activeRun = latestActiveRoot(snapshot?.runs ?? []);
  useEffect(() => {
    if (!snapshot?.participation || surface !== "workspace") return;
    const sequence = Math.max(-1, ...snapshot.messages.map((message) => message.sequence));
    if (sequence < 0) return;
    if (sequence <= snapshot.participation.lastReadSequence) return;
    const acknowledge = () => {
      if (document.visibilityState !== "visible") return;
      void api<{ lastReadSequence: number }>(
        `/v1/apps/${encodeURIComponent(snapshot.app.id)}/threads/${encodeURIComponent(snapshot.thread.id)}/read`,
        { method: "POST", body: JSON.stringify({ sequence }) },
      ).then((state) => {
        setThreadsByApp((current) => {
          const thread = current.get(snapshot.app.id);
          return thread?.id === snapshot.thread.id && (thread.latestMessageSequence ?? sequence) <= state.lastReadSequence
            ? new Map(current).set(snapshot.app.id, { ...thread, unread: false }) : current;
        });
        setSnapshot((current) => current?.thread.id === snapshot.thread.id && current.participation
        ? { ...current, participation: { ...current.participation, lastReadSequence: Math.max(current.participation.lastReadSequence, state.lastReadSequence) } }
        : current);
      }).catch(() => undefined);
    };
    acknowledge();
    document.addEventListener("visibilitychange", acknowledge);
    return () => document.removeEventListener("visibilitychange", acknowledge);
  }, [snapshot?.thread.id, snapshot?.cursor, snapshot?.participation?.lastReadSequence, surface]);
  useEffect(() => {
    if (!currentApp || !snapshot) return;
    const deploymentId =
      snapshot.deployment?.activeDeployment?.id ??
      [...(snapshot.outcomes ?? [])]
        .reverse()
        .find((outcome) => outcome.release?.deploymentId)?.release
        ?.deploymentId;
    if (
      !deploymentId ||
      releases.some((release) => release.id === deploymentId)
    )
      return;
    const refreshKey = `${currentApp.id}:${deploymentId}`;
    if (releaseRefreshRef.current === refreshKey) return;
    releaseRefreshRef.current = refreshKey;
    void loadReleases(currentApp.id).catch((error: unknown) =>
      notify(messageOf(error)),
    );
  }, [currentApp, loadReleases, notify, releases, snapshot]);

  async function reportProblem(title: string, summary: string) {
    if (!currentApp) return;
    await api(
      `/v1/apps/${encodeURIComponent(currentApp.id)}/incidents/report`,
      {
        method: "POST",
        body: requestBody({ title, summary, evidenceRefs: [] }),
      },
    );
    notify("The app agent is investigating.");
    if (currentThread) await refreshSnapshot(currentApp, currentThread);
  }

  /** Asks the app's Agent to fix an alert it didn't fix by itself, in the app's chat. */
  async function fixIncident(incident: IncidentView) {
    if (!currentApp) return;
    const app = currentApp;
    const path = `/v1/apps/${encodeURIComponent(app.id)}/incidents`;
    try {
      const result = await api<{ incident: IncidentView; threadId: string }>(
        `${path}/${encodeURIComponent(incident.id)}/repair`,
        { method: "POST", body: JSON.stringify({ requestId: browserRequestId() }) },
      );
      setIncidents((current) => current.map((item) => (item.id === result.incident.id ? result.incident : item)));
      notify("The app agent is fixing it.");
      // An app without a chat gets one for the repair; open it.
      if (currentThread?.id !== result.threadId) await selectApp(app, result.threadId, "workspace", "replace");
      else await refreshSnapshot(app, currentThread);
    } catch (error) {
      notify(messageOf(error));
      // The card shows what stopped this try.
      const refreshed = await api<{ incidents: IncidentView[] }>(path).catch(() => null);
      if (refreshed) setIncidents(refreshed.incidents);
    }
  }

  function openAgentSettings() {
    setSurface(currentApp ? "settings" : "new-settings");
    updateLocation(currentApp ? { app: appRouteKey(currentApp), surface: "settings" } : { surface: "new-settings" });
  }

  async function showReleaseHistory() {
    if (!currentApp) return;
    setReleaseError(null);
    try {
      await loadReleases(currentApp.id);
      setOpenReleaseId(null);
      setSurface("releases");
      updateLocation({
        app: appRouteKey(currentApp),
        surface: "releases",
      });
    } catch (error) {
      notify(messageOf(error));
    }
  }

  async function revertRelease(release: AgentRelease) {
    if (!currentApp) return;
    setBusy(true);
    setReleaseError(null);
    try {
      const operation = await api<OperationView>(
        `/v1/apps/${encodeURIComponent(currentApp.id)}/releases/${encodeURIComponent(release.id)}/revert`,
        {
          method: "POST",
          headers: {
            "idempotency-key": `agent-ui-revert-${browserRequestId()}`,
          },
        },
      );
      await followOperation(operation);
      const catalog = await loadCatalog();
      const updatedApp = catalog.apps.find((app) => app.id === currentApp.id);
      if (updatedApp) setCurrentApp(updatedApp);
      await loadReleases(currentApp.id);
      notify("The earlier version is live. Release history was kept.");
    } catch (error) {
      setReleaseError(
        `${messageOf(error)} The app remains on its current release.`,
      );
    } finally {
      setBusy(false);
    }
  }

  if (fatalError) {
    return (
      <FatalError message={fatalError} onRetry={() => void initialize()} />
    );
  }
  if (session === undefined || surface === "loading")
    return <Loading bootstrap={bootstrap} />;
  if (session === null) return <SignInRedirect bootstrap={bootstrap} />;
  if (planUpgrade) {
    // The plan leaves the hosted Agent out, so nothing here can run. Before
    // connecting an AI account, offer the plan that includes it or building
    // from your own AI.
    return <HostedAgentUpgrade bootstrap={bootstrap} hostedAgent={planUpgrade} />;
  }

  const connectionProvider = agentConnectionProvider(defaultHarnessVersion);
  if (needsAiConnection({ platformAiEnabled, connections, connectionProvider,
    // Without personal accounts only the server can tell whether the
    // organisation's accounts can run the viewer's work. With them, the
    // server also knows about an app owner's account that pays for an app
    // the viewer was invited to administer.
    ...(personalAiAccountsAllowed(bootstrap)
      ? { serverRunnable: session.aiConnectionChecked === true && session.aiConnectionRequired === false }
      : { serverRequired: session.aiConnectionRequired === true }) })) {
    // Without hosted AI nothing in Agent can run, so every surface opens on
    // connecting an account and there is no way past it until one connects.
    // Where only organisation admins connect accounts, it explains that instead.
    return (
      <>
        <ProviderSetup
          key="connect"
          bootstrap={bootstrap}
          screen="connect"
          providers={providers.filter((provider) => connectionProvider === null || provider.id === connectionProvider)}
          connections={connections}
          onConnectionsChanged={async () => {
            await loadCatalog();
          }}
          onBack={null}
          notify={notify}
          engineLabels={agentEngineLabels(defaultHarnessVersion)}
        />
        <Notice message={notice} />
      </>
    );
  }

  if (surface === "new-settings") {
    // App-owner harnesses choose Codex or Claude Code from the account, so
    // every provider is usable; other Codex harnesses still require OpenAI.
    const agentProvider = agentConnectionProvider(defaultHarnessVersion);
    const compatible = (provider: string) =>
      agentProvider === null || provider === agentProvider;

    return (
      <>
        <ProviderSetup
          bootstrap={bootstrap}
          screen="settings"
          providers={providers.filter((provider) => compatible(provider.id))}
          connections={connections.filter((connection) => compatible(connection.provider))}
          onConnectionsChanged={async () => {
            await loadCatalog();
          }}
          onBack={() => {
            goBackOr(() => showNewApp("replace", false));
          }}
          notify={notify}
          engineLabels={agentEngineLabels(defaultHarnessVersion)}
        />
        <AgentNotificationPrompt
          open={notificationPromptOpen}
          onOpenChange={setNotificationPromptOpen}
          notify={notify}
        />
        <Notice message={notice} />
      </>
    );
  }

  if (surface === "settings" && currentApp) {
    // AI accounts and the app's AI setting live in the dashboard; Agent links there.
    const appName = currentApp.name ?? "this app";
    const refreshAccounts = async () => {
      await loadCatalog();
    };
    return (
      <>
        <ProviderSetup
          bootstrap={bootstrap}
          screen="settings"
          providers={providers}
          connections={connections}
          providerBlocked={activeRun?.status === "paused" ? activeRun.blocker?.requiredAction ?? null : null}
          onConnectionsChanged={refreshAccounts}
          onBack={() => {
            goBackOr(() => {
              void selectApp(
                currentApp,
                currentThread?.id,
                "workspace",
                "replace",
              );
            });
          }}
          notify={notify}
          engineLabels={agentEngineLabels(defaultHarnessVersion)}
          appAi={{ appName, dashboardUrl: appAiDashboardUrl(bootstrap.appsUrl, currentApp.id) }}
        />

        <AgentNotificationPrompt
          open={notificationPromptOpen}
          onOpenChange={setNotificationPromptOpen}
          notify={notify}
        />
        <Notice message={notice} />
      </>
    );
  }

  if (surface === "releases" && currentApp) {
    return (
      <>
        <ReleaseHistory
          app={currentApp}
          releases={releases}
          openReleaseId={openReleaseId}
          busy={busy}
          error={releaseError}
          onBack={() =>
            void selectApp(
              currentApp,
              currentThread?.id,
              "workspace",
              "replace",
            )
          }
          onOpen={(releaseId) => {
            setOpenReleaseId(releaseId);
            updateLocation({
              app: appRouteKey(currentApp),
              surface: "releases",
              ...(releaseId ? { release: releaseId } : {}),
            });
          }}
          onRevert={(release) => void revertRelease(release)}
        />
        <Notice message={notice} />
      </>
    );
  }

  // A new chat's engine follows the author's default account, as the service
  // does when it creates the conversation; organisation accounts can decide
  // it too, which only the service sees. The picker's pick then names the
  // provider that pays (decision 0031).
  const defaultAccount = connections.find((connection) => connection.status === "connected" && connection.isDefault)
    ?? connections.find((connection) => connection.status === "connected");
  const newConversationProvider = serviceNewChatProvider !== undefined ? serviceNewChatProvider
    : harnessProvider(defaultHarnessVersion) && usesAppOwnerHarness(defaultHarnessVersion) &&
      (defaultAccount?.provider === "openai" || defaultAccount?.provider === "anthropic")
      ? defaultAccount.provider : harnessProvider(defaultHarnessVersion);

  return (
    <>
      <ConversationChat
        eventStream={agentEvents}
        // A pick names the provider that pays for it (decision 0031); the
        // service decides which providers and models the composer offers.
        newConversationModels={acceptsModelChoice(defaultHarnessVersion)}
        newConversationProvider={newConversationProvider}
        defaultHarnessVersion={defaultHarnessVersion}
        conversationId={conversationId}
        onSelected={(id, appId) => {
          beginConversationView(appId, id);
          setConversationId(id);
          if (appId) return;
          setCurrentApp(null);
          setCurrentThread(null);
          setSnapshot(null);
          setReleases([]);
          setIncidents([]);
          setSurface("workspace");
        }}
        onSnapshot={receiveSnapshot}
        onSubmitted={offerNotifications}
        onAttached={(app, threadId) => {
          void selectApp(app, threadId, "workspace", "replace");
          void refreshWorkingApps().catch(() => undefined);
        }}
        attentionAppIds={attentionAppIds}
        bootstrap={bootstrap}
        session={session}
        apps={apps}
        workingAppIds={workingAppIds}
        threadsByApp={threadsByApp}
        app={currentApp}
        incidents={incidents}
        releases={releases}
        focusMessageId={focusMessageId}
        draft={draft}
        offline={offline}
        busy={busy}
        onDraft={setDraft}
        onHome={() => { showNewApp(); setConversationId(null); }}
        onNewApp={() => { showNewApp(); setConversationId(null); }}
        onSelectApp={(app, location) =>
          void selectApp(
            app,
            location?.threadId,
            "workspace",
            "push",
            location?.messageId,
          )
        }
        onAgentSettings={openAgentSettings}
        onReleaseHistory={() => void showReleaseHistory()}
        onLoadRelease={async (releaseId) => {
          if (!currentApp) throw new Error("No app selected");
          const response = await api<{ release: AgentRelease }>(
            `/v1/apps/${encodeURIComponent(currentApp.id)}/releases/${encodeURIComponent(releaseId)}`,
          );
          setReleases((known) => [
            response.release,
            ...known.filter((release) => release.id !== response.release.id),
          ]);
          return response.release;
        }}
        onReportProblem={reportProblem}
        onFixIncident={fixIncident}
      />
      <AgentNotificationPrompt
        open={notificationPromptOpen}
        onOpenChange={setNotificationPromptOpen}
        notify={notify}
      />
      <Notice message={notice} />
    </>
  );
}

/** The person's plan leaves the hosted Agent out; unknown access opens Agent as usual. */
export function hostedAgentUpgrade(session: Pick<SessionView, "hostedAgent"> | null | undefined): HostedAgentView | null {
  return session?.hostedAgent && !session.hostedAgent.included ? session.hostedAgent : null;
}

/** First run without hosted AI: no connected account can run the Agent. */
export function needsAiConnection(input: {
  platformAiEnabled: boolean;
  connections: readonly Pick<ProviderConnectionView, "status" | "provider">[];
  connectionProvider: string | null;
  /** The session's `aiConnectionRequired`, used where people have no own accounts. */
  serverRequired?: boolean;
  /**
   * Where people have their own accounts: the server found another account
   * that runs the viewer's work, such as an app owner's account that pays
   * for an app the viewer administers.
   */
  serverRunnable?: boolean;
}): boolean {
  if (input.serverRequired !== undefined) return !input.platformAiEnabled && input.serverRequired;
  return !input.platformAiEnabled && !input.serverRunnable && !input.connections.some(
    (connection) => connection.status === "connected" &&
      (input.connectionProvider === null || connection.provider === input.connectionProvider),
  );
}

export function replyHasArrived(
  awaiting: AwaitingAgentReply,
  snapshot: ConversationSnapshot | null,
  liveText: string,
): boolean {
  if (liveText.trim()) return true;
  if (!snapshot || snapshot.app.id !== awaiting.appId) return false;
  if (snapshot.thread.id !== awaiting.threadId) return false;
  const known = new Set(awaiting.knownAgentMessageIds);
  if (
    snapshot.messages.some(
      (message) => message.role === "agent" && !known.has(message.id),
    )
  ) {
    return true;
  }
  const sentAt = timestamp(awaiting.sentAt) - 1_000;
  const relevantRuns = snapshot.runs.filter(
    (run) =>
      run.parentRunId === null &&
      timestamp(run.createdAt) >= sentAt,
  );
  if (
    snapshot.question &&
    relevantRuns.some((run) => run.id === snapshot.question?.runId)
  ) {
    return true;
  }
  return relevantRuns.some((run) =>
    ["completed", "failed", "cancelled"].includes(run.status),
  );
}

function Loading({ bootstrap }: { bootstrap: AgentBootstrap }) {
  return (
    <div className="a3-theme a3-loading-screen" role="status">
      <img
        src={bootstrap.logoUrl}
        alt=""
        aria-hidden="true"
        width="32"
        height="32"
      />
      <Spinner aria-label="Opening Agent" />
      <span>Opening your app agents…</span>
    </div>
  );
}

function FatalError({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="a3-theme a3-fatal-screen">
      <AlertTriangle aria-hidden="true" />
      <h1>The agent didn’t open</h1>
      <p>{message}</p>
      <Button
        type="button"
        className="a3-action a3-action-primary"
        onClick={onRetry}
      >
        <RefreshCw aria-hidden="true" /> Try again
      </Button>
    </div>
  );
}

function Notice({ message }: { message: string }) {
  return (
    <div
      className="a3-notice"
      data-visible={Boolean(message)}
      role="status"
      aria-live="polite"
    >
      {message}
    </div>
  );
}

function messageOf(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "The request could not be completed.";
}

function readRoute(): {
  appId: string | null;
  threadId: string | null;
  messageId: string | null;
  surface: string | null;
  releaseId: string | null;
  platformReview: boolean;
} {
  return parseAgentRoute(window.location.search, window.location.pathname);
}

export function parseAgentRoute(searchValue: string, pathname = "/"): {
  appId: string | null;
  threadId: string | null;
  messageId: string | null;
  surface: string | null;
  releaseId: string | null;
  platformReview: boolean;
} {
  const search = new URLSearchParams(searchValue);
  const pathAppId = pathAppKey(pathname);
  return {
    // Keep accepting the old query form for existing links, then replace it
    // with the canonical path as soon as the app opens.
    appId: search.get("app") ?? pathAppId,
    threadId: search.get("thread"),
    messageId: search.get("message"),
    surface: search.get("surface"),
    releaseId: search.get("release"),
    platformReview: search.get("review") === "admin",
  };
}

export function agentRoutePath(values: {
  app?: string;
  thread?: string;
  message?: string;
  surface?: string;
  release?: string;
}): string {
  const search = new URLSearchParams();
  if (values.thread) search.set("thread", values.thread);
  if (values.message) search.set("message", values.message);
  if (values.surface && values.surface !== "new" && values.surface !== "apps") {
    search.set("surface", values.surface);
  }
  if (values.release) search.set("release", values.release);
  const query = search.toString();
  const pathname = values.app ? `/${encodeURIComponent(values.app)}` : "/";
  return query ? `${pathname}?${query}` : pathname;
}

function pathAppKey(pathname: string): string | null {
  const match = /^\/([^/]+)\/?$/.exec(pathname);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

function appRouteKey(app: AgentAppView): string {
  return app.slug || app.id;
}

function conversationViewMatches(
  view: ConversationView,
  appId: string,
  threadId: string | null,
  token = view.token,
): boolean {
  return (
    view.token === token && view.appId === appId && view.threadId === threadId
  );
}

function setMembership(
  current: ReadonlySet<string>,
  value: string,
  included: boolean,
): Set<string> {
  if (current.has(value) === included) return current as Set<string>;
  const next = new Set(current);
  if (included) next.add(value);
  else next.delete(value);
  return next;
}

export function findRouteApp(
  apps: readonly AgentAppView[],
  routeAppId: string | null,
): AgentAppView | undefined {
  if (!routeAppId) return undefined;
  return apps.find(
    (app) => app.id === routeAppId || app.slug === routeAppId,
  );
}

export function orderAppsByRecentConversation(
  apps: readonly AgentAppView[],
  threadsByApp: ReadonlyMap<string, ConversationThread | null>,
): AgentAppView[] {
  return apps
    .map((app, index) => ({
      app,
      index,
      activityAt: timestamp(
        threadsByApp.get(app.id)?.updatedAt ??
          app.updatedAt ??
          app.createdAt ??
          "",
      ),
    }))
    .sort(
      (left, right) =>
        right.activityAt - left.activityAt || left.index - right.index,
    )
    .map(({ app }) => app);
}

function preserveAppOrder(
  apps: readonly AgentAppView[],
  current: readonly AgentAppView[],
): AgentAppView[] {
  const rank = new Map(current.map((app, index) => [app.id, index]));
  return apps
    .map((app, index) => ({ app, index, rank: rank.get(app.id) }))
    .sort(
      (left, right) =>
        (left.rank ?? current.length + left.index) -
        (right.rank ?? current.length + right.index),
    )
    .map(({ app }) => app);
}

function mergeRefreshedApps(
  refreshed: readonly AgentAppView[],
  current: readonly AgentAppView[],
): AgentAppView[] {
  const incoming = new Map(refreshed.map((app) => [app.id, app]));
  return [
    ...current.map((app) => incoming.get(app.id) ?? app),
    ...refreshed.filter((app) => !current.some((known) => known.id === app.id)),
  ];
}

export function promoteApp(
  apps: readonly AgentAppView[],
  appId: string,
): AgentAppView[] {
  const selected = apps.find((app) => app.id === appId);
  return selected
    ? [selected, ...apps.filter((app) => app.id !== appId)]
    : [...apps];
}

function timestamp(value: string): number {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function updateLocation(
  values: {
    app?: string;
    message?: string;
    surface?: string;
    release?: string;
  },
  historyMode: HistoryMode = "push",
) {
  if (historyMode === "none") return;
  const routePath = agentRoutePath(values);
  const path = routePath;
  const currentState = historyState();
  if (`${window.location.pathname}${window.location.search}` === path) return;
  const currentIndex = agentHistoryIndex();
  const nextState = {
    ...currentState,
    [AGENT_HISTORY_INDEX]:
      historyMode === "push" ? currentIndex + 1 : currentIndex,
  };
  if (historyMode === "replace") {
    window.history.replaceState(nextState, "", path);
  } else {
    window.history.pushState(nextState, "", path);
  }
}

function ensureAgentHistoryEntry(): void {
  const state = historyState();
  if (typeof state[AGENT_HISTORY_INDEX] === "number") return;
  window.history.replaceState(
    { ...state, [AGENT_HISTORY_INDEX]: 0 },
    "",
    `${window.location.pathname}${window.location.search}`,
  );
}

function goBackOr(fallback: () => void): void {
  if (agentHistoryIndex() > 0) {
    window.history.back();
  } else {
    fallback();
  }
}

function agentHistoryIndex(): number {
  const value = historyState()[AGENT_HISTORY_INDEX];
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0
    ? value
    : 0;
}

function historyState(): Record<string, unknown> {
  const state: unknown = window.history.state;
  return state && typeof state === "object" && !Array.isArray(state)
    ? (state as Record<string, unknown>)
    : {};
}
